'use client';

import { useState, useEffect, useRef } from 'react';
import { PixumEnvUtils } from 'utils';
import { buildMentionMeUrl } from './utils/urlBuilder';
import type { MentionMeNode } from 'data-layer';

/**
 * Hook to build the Mention Me script URL once PIXUM_ENV has been extended.
 *
 * PixumEnvExtensionApplier runs inside a Suspense boundary that streams in
 * asynchronously, so PIXUM_ENV.USER may not be populated at mount time.
 * We defer URL construction until notifyExtended() has been called.
 *
 * If the initial URL was built for an anonymous user, the hook reloads the
 * page on the next notifyExtended() that carries authenticated user data so
 * the MentionMe SDK can reinitialize. The SDK does not support runtime
 * reinitialization, so a full reload is required.
 *
 * The reload subscription is registered in a separate mount-only effect so
 * that config reference changes after router.refresh() do not accidentally
 * remove the listener before login is detected.
 *
 * @param locale - The current locale (e.g., 'de-DE', 'en-US')
 * @param config - MentionMe configuration from CMS
 * @returns The Mention Me script URL (or null before the env is extended)
 */
export function useMentionMe(locale: string, config?: MentionMeNode): string | null {
  const [scriptUrl, setScriptUrl] = useState<string | null>(null);
  const hasBuiltUrlRef = useRef(false);
  const wasAnonymousRef = useRef(false);

  useEffect(() => {
    const cleanupOnce = PixumEnvUtils.onceExtended(() => {
      if (hasBuiltUrlRef.current) return;

      hasBuiltUrlRef.current = true;

      const user = PixumEnvUtils.get('USER');
      const isAuthenticated = user?.isAuthenticated ?? false;
      const { email, firstName, lastName } = user ?? {};

      const url = buildMentionMeUrl({
        pathname: window.location.pathname,
        locale,
        config,
        user: isAuthenticated ? { isAuthenticated, email, firstName, lastName } : undefined,
      });

      setScriptUrl(url);
      wasAnonymousRef.current = !isAuthenticated;
    });

    return cleanupOnce;
  }, [locale, config]);

  // Separate mount-only effect so config reference changes after router.refresh()
  // do not remove this listener before a login event is detected.
  useEffect(() => {
    const unsubscribe = PixumEnvUtils.subscribeExtended(() => {
      if (wasAnonymousRef.current && PixumEnvUtils.get('USER')?.isAuthenticated) {
        setScriptUrl(null);
        window.location.reload();
      }
    });

    return unsubscribe;
  }, []);

  return scriptUrl;
}
